import React, { useCallback, useMemo, useState } from 'react';
import {
  Empty,
  RecordContextProvider,
  ReferenceManyField,
  SelectField,
  TextField,
  useGetOne,
  usePermissions,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';

import { baseRolesChoices } from '../../constants/baseRoles';
import InfoPopover from '../layout/InfoPopover';
import { PermissionRole, ReformedPermissionRole } from './type';
import { PermissionAccordionShow } from './PermissionAccordion/PermissionAccordion';
import resourceNames from '../../constants/resourceNames';
import { ValueOf } from '../../utilities/types';
import { AssignedAPIKeysDatagrid } from '../apiKeys/ApiKeyList';
import Pagination from '../layout/Pagination';
import { AssignedMembersDatagrid } from '../member/MemberList';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import AssignMemberPermission from '../member/AssignMemberPermission';
import AssignApiKeyPermission from '../apiKeys/AssignApiKeyPermission';
import PermissionRolesDelete from './PermissionRolesDelete';
import { iconNameIconMap } from '../../constants/permissionIconNames';

const tabs = {
  PERMISSIONS: 'permissions',
  ASSIGNED_MEMBERS: 'assigned-members',
  ASSIGNED_API_KEYS: 'assigned-api-keys',
};
type TabValues = ValueOf<typeof tabs>;

const PermissionRolesShow = ({
  id,
  setEdit,
  setSelectedRole,
}: {
  id: string;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedRole: (newRole: string | undefined) => void;
}) => {
  const theme = useTheme();
  const { data, isLoading } = useGetOne<PermissionRole>(resourceNames.PERMISSION_ROLES, { id });
  const { data: memberCount, isLoading: isMemberCountLoading } = useGetOne<{ id: string; count: string }>(`permission-roles/${id}/members/count`, { id: '' });
  const { data: apiKeysCount, isLoading: isApiKeysCountLoading } = useGetOne<{ id: string; count: string }>(`permission-roles/${id}/api-keys/count`, { id: '' });

  const transformedData: ReformedPermissionRole | undefined = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      permissions: data.permissions.reduce((acc, { name, attributes }) => ({
        ...acc,
        [name]: {
          enabled: true,
          attributes,
        },
      }), {}),
    };
  }, [data]);

  const [tab, setTab] = useState(tabs.PERMISSIONS);
  const handleTabChange = useCallback((
    _event: React.SyntheticEvent<Element, Event>,
    newValue: TabValues,
  ) => {
    setTab(newValue);
  }, []);
  const { permissions } = usePermissions<Actions[]>();

  const onDelete = useCallback(() => {
    setSelectedRole(undefined);
    setEdit(false);
  }, [setEdit, setSelectedRole]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) return <Empty />;

  return (
    <RecordContextProvider value={transformedData}>
      <Card variant="outlined" sx={{ padding: 4 }}>
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" marginBottom={3} gap={2}>
            <Box display="flex" gap={2} alignItems="center" color={theme.palette.primary.main}>
              {/* //TODO: Remove check once we have migrated the data */}
              {data.iconName && React.createElement(
                iconNameIconMap[data.iconName],
                { fontSize: 'large' },
              )}
              <TextField source="name" variant="h2" color="primary" sx={{ wordBreak: 'break-word' }} />
            </Box>
            <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
              <PermissionRolesDelete onDelete={onDelete} />
              {hasAccess(permissions, actions.PERMISSION_ROLE_UPDATE) && (
                <Button
                  startIcon={<CreateIcon color="secondary" />}
                  variant="outlined"
                  onClick={() => setEdit(true)}
                >
                  Edit
                </Button>
              )}
            </Box>
          </Box>
          <TextField source="description" fontSize="1rem" sx={{ wordBreak: 'break-word' }} />
          <Box marginY={3}>
            <Tabs value={tab} onChange={handleTabChange}>
              <Tab label="Permissions" value={tabs.PERMISSIONS} />
              <Tab label={!isMemberCountLoading && memberCount?.count ? `Assigned members (${memberCount.count})` : 'Assigned members'} value={tabs.ASSIGNED_MEMBERS} />
              <Tab label={!isApiKeysCountLoading && apiKeysCount?.count ? `Assigned api keys (${apiKeysCount.count})` : 'Assigned api keys'} value={tabs.ASSIGNED_API_KEYS} />
            </Tabs>
            <Divider />
          </Box>
          {tab === tabs.PERMISSIONS && (
            <Box display="flex" flexDirection="column" gap={4}>
              <PermissionAccordionShow />
              <Box
                bgcolor={theme.palette.greenGray.main}
                borderRadius={1}
                padding={4}
                display="flex"
                alignItems="center"
              >
                <Typography fontSize="1rem">
                  Receive feature updates from base role:
                  {' '}
                  <SelectField
                    source="baseRole"
                    fontWeight="bold"
                    fontSize="1rem"
                    choices={baseRolesChoices}
                  />
                </Typography>
                <InfoPopover
                  content="Custom roles stay aligned with new product features by selecting a Base Role. When new actions become available, Pingwire updates the Base Role's permissions and any custom roles based on it will automatically receive those same updates."
                  title="Base role"
                />
              </Box>
            </Box>
          )}
          {tab === tabs.ASSIGNED_MEMBERS && (
            <Box display="flex" flexDirection="column" gap={4}>
              <Card variant="outlined">
                <ReferenceManyField
                  reference={resourceNames.MEMBERS}
                  target="permissionRoleIds"
                  pagination={<Pagination disableEmptyText />}
                  sort={{ field: 'id', order: 'DESC' }}
                  perPage={10}
                >
                  <AssignedMembersDatagrid />
                </ReferenceManyField>
              </Card>
              <Box display="flex" justifyContent="flex-end">
                <AssignMemberPermission />
              </Box>
            </Box>
          )}
          {tab === tabs.ASSIGNED_API_KEYS && (
            <Box display="flex" flexDirection="column" gap={4}>
              <Card variant="outlined">
                <ReferenceManyField
                  reference={resourceNames.API_KEYS}
                  target="permissionRoleIds"
                  pagination={<Pagination disableEmptyText />}
                  sort={{ field: 'createdAt', order: 'DESC' }}
                  perPage={10}
                >
                  <AssignedAPIKeysDatagrid />
                </ReferenceManyField>
              </Card>
              <Box display="flex" justifyContent="flex-end">
                <AssignApiKeyPermission />
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </RecordContextProvider>
  );
};

export default PermissionRolesShow;
